// logic/integrationLogic/config/integrationEndpoints.js - Updated with AWS Config endpoints
// Import centralized API configuration
import { API_BASE_URL } from '../../../shared/config/apiConfig';

const INTEGRATION_BASE = `${API_BASE_URL}/integrations`;
const ORG_CONFIG_BASE = `${API_BASE_URL}/organisations`;

/**
 * Integration API Endpoints Configuration
 * Separated: AWS/Bedrock use OrganizationConfigController, Atlassian/XRAY use IntegrationController
 */
export const INTEGRATION_ENDPOINTS = {
  // Base URL
  BASE: INTEGRATION_BASE,

  // Get all integrations for a tenant (still used for Atlassian/XRAY)
  getAllIntegrations: (tenantId) => `${INTEGRATION_BASE}/${tenantId}`,

  // Get specific integration credentials (Atlassian/XRAY only)
  getIntegration: (tenantId, integrationType) => 
    `${INTEGRATION_BASE}/${tenantId}/${integrationType}`,

  // Update integration credentials (Atlassian/XRAY only)
  updateIntegration: (tenantId, integrationType) => 
    `${INTEGRATION_BASE}/${tenantId}/${integrationType}`,

  // Delete/Reset integration credentials (Atlassian/XRAY only)
  deleteIntegration: (tenantId, integrationType) => 
    `${INTEGRATION_BASE}/${tenantId}/${integrationType}`,

  // Verify integration credentials before going into the DB (Atlassian/XRAY only)
  verifyIntegration: (tenantId, integrationType) => 
    `${INTEGRATION_BASE}/${tenantId}/${integrationType}/verify-new`,

  // Verify integration credentials currently set for the tenant (Atlassian/XRAY only)
  verifyCurrentIntegration: (tenantId, integrationType) => 
    `${INTEGRATION_BASE}/${tenantId}/${integrationType}/verify-current`,

  // Mark user as logged in (first-time setup complete)
  markAsLoggedIn: (tenantId) => 
    `${INTEGRATION_BASE}/${tenantId}/logged-in`
};

/**
 * NEW: AWS Configuration Endpoints (OrganizationConfigController)
 * These handle AWS S3 and Bedrock configurations stored in Organization entity
 * Note: Save endpoints now automatically verify configuration and return verification status
 */
export const AWS_CONFIG_ENDPOINTS = {
  // Get current AWS configuration (both S3 and Bedrock)
  getAWSConfig: (orgId) => `${ORG_CONFIG_BASE}/${orgId}/aws-config`,

  // Configure custom S3 buckets (auto-verifies and returns verificationStatus)
  configureS3: (orgId) => `${ORG_CONFIG_BASE}/${orgId}/aws-config/s3`,

  // Configure custom Bedrock (auto-verifies and returns verificationStatus)
  configureBedrock: (orgId) => `${ORG_CONFIG_BASE}/${orgId}/aws-config/bedrock`,

  // Re-verify existing S3 configuration
  reVerifyS3: (orgId) => `${ORG_CONFIG_BASE}/${orgId}/aws-config/s3/verify`,

  // Re-verify existing Bedrock configuration
  reVerifyBedrock: (orgId) => `${ORG_CONFIG_BASE}/${orgId}/aws-config/bedrock/verify`,

  // Reset to default Tonic S3 buckets
  resetS3: (orgId) => `${ORG_CONFIG_BASE}/${orgId}/aws-config/s3`,

  // Reset to default Tonic Bedrock
  resetBedrock: (orgId) => `${ORG_CONFIG_BASE}/${orgId}/aws-config/bedrock`
};

/**
 * Integration Types
 */
export const INTEGRATION_TYPES = {
  AWS: 'AWS',
  BEDROCK: 'BEDROCK',
  ATLASSIAN: 'ATLASSIAN',
  XRAY: 'XRAY',
  COGNITO: 'COGNITO',
  TEST_ENGINE: 'TEST_ENGINE'
};

/**
 * Integration Field Configurations
 * UPDATED: AWS and BEDROCK fields now match Organization entity structure
 */
export const INTEGRATION_FIELDS = {
  AWS: [
    { 
      name: 'projectsBucketName', 
      label: 'Projects S3 Bucket Name', 
      type: 'text', 
      required: true,
      placeholder: 'yourorg-test-engine-dev',
      helpText: 'S3 bucket name for project/test data storage'
    },
    { 
      name: 'projectsBucketRoleArn', 
      label: 'Projects Bucket Role ARN', 
      type: 'text', 
      required: true,
      placeholder: 'arn:aws:iam::123456789012:role/TonicAccessRole',
      helpText: 'IAM role ARN for accessing the Projects bucket'
    }
  ],
  BEDROCK: [
    { 
      name: 'bedrockRoleArn', 
      label: 'Bedrock Role ARN', 
      type: 'text', 
      required: true,
      placeholder: 'arn:aws:iam::123456789012:role/YourBedrockRole',
      helpText: 'IAM role ARN for accessing AWS Bedrock'
    },
    { 
      name: 'bedrockModelType', 
      label: 'Model Type', 
      type: 'text', 
      required: true,
      placeholder: 'anthropic.claude-v2',
      helpText: 'Bedrock model identifier (e.g., anthropic.claude-v2)'
    },
    { 
      name: 'bedrockRegion', 
      label: 'AWS Region', 
      type: 'text', 
      required: true,
      defaultValue: 'us-east-1',
      placeholder: 'us-east-1',
      helpText: 'AWS region where Bedrock is enabled'
    }
  ],
  ATLASSIAN: [
    { name: 'email', label: 'Email', type: 'email', required: true },
    { name: 'apiToken', label: 'API Token', type: 'password', required: true },
    { name: 'siteUrl', label: 'Site URL', type: 'text', required: true, placeholder: 'https://yoursite.atlassian.net' }
  ],
  XRAY: [
    { name: 'clientId', label: 'Client ID', type: 'text', required: true },
    { name: 'clientSecret', label: 'Client Secret', type: 'password', required: true },
    { name: 'projectKey', label: 'Project Key', type: 'text', required: true, placeholder: 'e.g. PROJ' }
  ],
  COGNITO: [
    { name: 'userPoolId', label: 'User Pool ID', type: 'text', required: true, placeholder: 'us-east-1_abc123' },
    { name: 'cognitoRegion', label: 'Region', type: 'awsRegion', required: true, placeholder: 'us-east-1' },
    { name: 'cognitoClientId', label: 'App Client ID', type: 'text', required: true },
    { name: 'cognitoClientSecret', label: 'App Client Secret', type: 'password', required: true },
    { name: 'hostedUiDomain', label: 'Hosted UI Domain', type: 'text', required: true, placeholder: 'tenant.auth.us-east-1.amazoncognito.com' },
    { name: 'autoProvision', label: 'Auto Provision Users', type: 'boolean', required: false }
  ],
  TEST_ENGINE: [
    {
      name: 'url',
      label: 'API Gateway URL',
      type: 'text',
      required: true,
      placeholder: 'https://api.yourorg.io/tonic',
      helpText: 'Base URL of your Test Engine API Gateway. Must start with https:// and must not include a trailing slash. This is the endpoint configured during core onboarding.'
    },
    {
      name: 'apiKey',
      label: 'API Key',
      type: 'password',
      required: true,
      placeholder: '••••••••••••••••••••••••',
      helpText: 'The API key provisioned for your organisation during core onboarding. The key must have the integration set to enabled (isActive: true) on the backend before verification will succeed.'
    }
  ]
};

/**
 * Integration Metadata
 */
export const INTEGRATION_METADATA = {
  AWS: {
    title: 'AWS',
    description: 'Configure AWS credentials for S3 storage and cloud infrastructure',
    gradient: 'linear-gradient(135deg, #FF9900, #FF6600)',
    accentColor: '#FF9900',
    mandatory: true
  },
  BEDROCK: {
    title: 'AWS Bedrock',
    description: 'Configure AWS Bedrock for AI model inference',
    gradient: 'linear-gradient(135deg, #05A88D, #00D4B6)',
    accentColor: '#05A88D',
    mandatory: true
  },
  ATLASSIAN: {
    title: 'Atlassian',
    description: 'Connect to Jira and Confluence (Optional)',
    gradient: 'linear-gradient(135deg, #1868DB, #1A82FF)',
    accentColor: '#1868DB',
    mandatory: false
  },
  XRAY: {
    title: 'XRAY',
    description: 'XRAY test management integration (Optional)',
    gradient: 'linear-gradient(135deg, #2BB34B, #1E8F3C)',
    accentColor: '#2BB34B',
    mandatory: false
  },
  COGNITO: {
    title: 'Cognito',
    description: 'Bring your own Cognito user pool for SSO login (Optional)',
    gradient: null,
    accentColor: null,
    mandatory: false
  },
  TEST_ENGINE: {
    title: 'Test Engine',
    description: 'Connect your Test Engine gateway to enable automation script execution. Requires the API Gateway URL and an API key with isActive set to true on the backend. Configure this after core onboarding is complete.',
    gradient: null,
    accentColor: null,
    mandatory: false
  }
};

/**
 * Verification Status Constants
 */
export const VERIFICATION_STATUS = {
  VALID: 'valid',
  INVALID: 'invalid',
  UNKNOWN: 'unknown',
  PENDING: 'pending',
  NOT_VERIFIED: 'not_verified'
};

/**
 * Supplied By Constants
 * Indicates whether credentials are client-supplied or system-supplied (Tonic)
 */
export const SUPPLIED_BY = {
  CLIENT: 'client_supplied',
  SYSTEM: 'tonic_supplied'
};